import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Trash2 } from 'lucide-react';
import { Button, CardTitle, Message } from '../admin/ui';
import * as Api from './PersonAdminApi';
import { errorText } from './personAdminModel';
import { referenceRows } from './referenceLabels';

// Deleting a person (admins only; owner 2026-10-25): only for a row with nothing attached - a typo, a
// wrong import, a test entry. The card asks the backend first and shows either what is attached (and
// that a duplicate is merged instead) or what goes with the person, and deletes after one
// confirmation. Afterwards the screen returns to the people list.

const Ul = ({ children }) => <ul style={{ margin: '.3rem 0 0', paddingLeft: '1.1rem', fontSize: '.85rem', lineHeight: 1.5 }}>{children}</ul>;

const DeleteCard = ({ personId }) => {
  const intl = useIntl();
  const t = useCallback((id, defaultMessage, values) => intl.formatMessage({ id, defaultMessage }, values), [intl]);
  const navigate = useNavigate();
  const [check, setCheck] = useState(null);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Api.fetchDeletion(personId)
      .then((c) => { if (!cancelled) setCheck(c); })
      .catch((e) => { if (!cancelled) setError(errorText(e, t('personAdmin.delete.checkFailed', 'Could not check whether this person can be deleted.'))); });
    return () => { cancelled = true; };
  }, [personId, t]);

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await Api.deletePerson(personId);
      navigate('/admin/people');
    } catch (e) {
      setError(errorText(e, t('personAdmin.delete.failed', 'The person could not be deleted.')));
      setAsking(false);
    } finally {
      setBusy(false);
    }
  };

  const blockers = check ? referenceRows(t, check.blockers) : [];
  const removed = check ? referenceRows(t, check.removedWithIt) : [];

  return (
    <>
      <CardTitle><FormattedMessage id="personAdmin.delete.title" defaultMessage="Delete this person" /></CardTitle>
      {error && <Message error role="alert">{error}</Message>}
      {check && !check.deletable && (
        <div style={{ fontSize: '.85rem' }}>
          <FormattedMessage id="personAdmin.delete.blocked"
            defaultMessage="This person cannot be deleted, because something is attached:" />
          <Ul>{blockers.map((row) => <li key={row.key}>{row.label}: {row.count}</li>)}</Ul>
          <p style={{ margin: '.6rem 0 0', fontSize: '.82rem', color: '#64748b' }}>
            <FormattedMessage id="personAdmin.delete.blockedHint"
              defaultMessage="Delete is only for rows that should never have existed. A duplicate of a real person is merged instead; a spot on the program is freed there first." />
          </p>
        </div>
      )}
      {check && check.deletable && (
        <div style={{ fontSize: '.85rem' }}>
          <FormattedMessage id="personAdmin.delete.possible"
            defaultMessage="Nothing is attached to this person. Deleting removes the row for good, with its own history:" />
          <Ul>
            {removed.map((row) => <li key={row.key}>{row.label}: {row.count}</li>)}
            {check.clearedProposals > 0 && (
              <li>
                <FormattedMessage id="personAdmin.delete.proposals"
                  defaultMessage="{count, plural, one {# removed proposal on the program loses the name} other {# removed proposals on the program lose the name}}"
                  values={{ count: check.clearedProposals }} />
              </li>
            )}
          </Ul>
          {!asking ? (
            <Button type="button" danger style={{ marginTop: '.75rem' }} disabled={busy} onClick={() => setAsking(true)}>
              <Trash2 size={16} /><FormattedMessage id="personAdmin.delete.button" defaultMessage="Delete…" />
            </Button>
          ) : (
            <Message warn role="alertdialog" style={{ marginTop: '.75rem' }}>
              <div>
                <FormattedMessage id="personAdmin.delete.confirm" defaultMessage="Delete {name} for good? This cannot be undone."
                  values={{ name: <strong>{check.name || `#${personId}`}</strong> }} />
              </div>
              <div style={{ marginTop: '.6rem', display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
                <Button type="button" danger disabled={busy} onClick={remove}>
                  <FormattedMessage id="personAdmin.delete.confirmYes" defaultMessage="Delete for good" />
                </Button>
                <Button type="button" secondary disabled={busy} onClick={() => setAsking(false)}>
                  <FormattedMessage id="personAdmin.delete.confirmNo" defaultMessage="Cancel" />
                </Button>
              </div>
            </Message>
          )}
        </div>
      )}
    </>
  );
};

export default DeleteCard;
